import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MeetUpsAPI } from "../api/Meet-ups";
import { filterArrayByString } from "../componets/helper";
import { DataGrid } from "@mui/x-data-grid";
import { Box, IconButton, Stack, useMediaQuery } from "@mui/material";
import GridCustomHeader from "../componets/AppLayout/GridCustomHeader";
import AppLayout from "../componets/AppLayout/AppLayout";
import AddMeetupModal from "../componets/Modal/AddMeetupModal";
import VisibilityIcon from "../assets/SVGs/Login/VisibilityIcon";
import EditMeetUpModal from "../componets/Modal/EditMeetUpModal";
import { defaultEditFormState } from "../componets/constant";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useAppContext } from "../context/AppContext";

const MeetUps = () => {
  const { openSnack } = useAppContext();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  const [openEditModal, setOpenEditModal] = useState(defaultEditFormState);

  const handleSearch = (searchString) => {
    setSearchText(searchString);
  };

  const columns = [
    {
      field: "meetup_date",
      headerName: "Meet-Up Date",
      flex: 1,
      minWidth: isMobileTable ? 170 : undefined,
      editable: true,
      type: "date",
      valueGetter: (params) => {
        return dayjs(params).toDate(); // returns a native JS Date
      },
      renderCell: (params) => {
        return params.value ? dayjs(params.value).format("MM/DD/YYYY") : "";
      },
      renderEditCell: (params) => {
        const handleChange = (event) => {
          const newValue = event;
          params.api.setEditCellValue({
            id: params.id,
            field: "meetup_date",
            value: newValue,
          });
        };

        return (
          <DatePicker
            value={dayjs(params.value)}
            onChange={handleChange}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "meetup_date",
                name: "meetup_date",
                className: "login-textField",
                size: "small",
              },
            }}
          />
        );
      },
    },
    {
      field: "added_active_members",
      headerName: "Members",
      flex: 1,
      minWidth: isMobileTable ? 250 : undefined,
      renderCell: (params) =>
        params?.row?.added_active_members
          ?.map((member) => `${member.first_name} ${member.last_name}`)
          .join(", ") || "No members",
      valueGetter: (params) =>
        params
          ?.map((member) => `${member.first_name} ${member.last_name}`)
          .join(", ") || "",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() =>
            setOpenEditModal({
              open: true,
              id: params?.row?.id,
              title: params?.row?.meetup_date,
            })
          }
        >
          <VisibilityIcon
            sx={{
              color: "#64748B",
            }}
          />
        </IconButton>
      ),
    },
  ];

  const fetch = async () => {
    setIsLoading(true);
    try {
      setData(await MeetUpsAPI.getMeets());
    } catch (e) {
      console.log(e.response);
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    isLoading && fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const getRowsData = () => {
    const dummyRows = [...(data?.items || [])];

    return filterArrayByString(dummyRows || [], searchText);
  };

  const processRowUpdate = async (newRow, oldRow) => {
    try {
      await editMeetUp(newRow);
      return newRow; // Return the updated row
    } catch (error) {
      console.error("Row update failed:", error);
      return oldRow; // Revert to the old row if the update fails
    }
  };

  const editMeetUp = async (form) => {
    const formObj = {
      meetup_date: dayjs(form.meetup_date).format("YYYY-MM-DD"),
    };
    try {
      await MeetUpsAPI.updateMeet(formObj, form?.id);
      openSnack("Meet-Up information updated successfully.", "success");
      fetch();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  return (
    <AppLayout>
      <Box
        sx={{
          width: "100%",
          padding: isMobileTable ? "8px" : "24px",
        }}
      >
        {/* Custom Header */}
        <GridCustomHeader
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          searchText={searchText}
          handleSearch={handleSearch}
          setOpenModal={setOpenModal}
          buttonText="Add New Meet-Up"
          headerName="Meet-Ups"
        />

        {/* Data Grid */}
        <Stack
          sx={{
            width: "100%",
            overflow: "hidden",
            "& .MuiDataGrid-root": {
              borderRadius: "8px",
            },
          }}
          className="customDataGridFigma"
        >
          <DataGrid
            rows={getRowsData() || []}
            columns={columns}
            loading={isLoading}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[10, 15, 20, 25]}
            checkboxSelection={false}
            rowSelection={false}
            rowHeight={48}
            columnHeaderHeight={40}
            getRowClassName={(params) => {
              const customClassName =
                params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
              return ` ${customClassName}`.trim();
            }}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "linear-progress",
              },
            }}
            localeText={{
              noRowsLabel: "No meet-ups found.",
            }}
            processRowUpdate={processRowUpdate}
            disableVirtualization
          />
        </Stack>
        {openModal && (
          <AddMeetupModal
            open={openModal}
            handleClose={() => setOpenModal(false)}
            setIsLoading={setIsLoading}
          />
        )}

        {openEditModal?.open && (
          <EditMeetUpModal
            open={openEditModal?.open}
            id={openEditModal?.id}
            handleClose={() => setOpenEditModal(defaultEditFormState)}
            setIsLoading={setIsLoading}
            title={dayjs(openEditModal?.title).format("MM/DD/YYYY")}
          />
        )}
      </Box>
    </AppLayout>
  );
};

export default MeetUps;
